import { ClipboardModule } from '@angular/cdk/clipboard';
import {
  Component,
  ElementRef,
  inject,
  InjectionToken,
  input,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal,
  ViewChild,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { type ChartSplitPiece, splitAtChartBlocks } from '@shared/chart-split';
import { MarkdownPipe } from 'ngx-markdown';
import { fromEvent, Subscription, tap, throttleTime } from 'rxjs';
import { AnalyticsService } from '../../../../../analytics';
// Type-only: a value import would pull `@shared/charts` into the initial
// bundle. The module itself loads through `CHART_COPY_LOADER`.
import type * as fromChartCopy from '../../../../../charts/chart.copy';
import {
  type ChartCopyPiece,
  chartNoticePiece,
  COPY_UNAVAILABLE_KEY,
  ILLUSTRATIVE_KEY,
} from '../../../../../charts/chart.copy-notice';

const COPIED_TEXT_TIMEOUT_MS = 1500;
const TOOLTIP_HIDE_FADE_MS = 200;
const SCROLL_THROTTLE_TIME_MS = 200;

/**
 * App-authored chart strings (Article XIV). Constant keys only; the caption
 * and notice keys come from `chart.copy-notice.ts`, shared with the lazy chunk.
 */
const SERIES_KEY = 'CHAT.CHART.TABLE.SERIES';
const X_KEY = 'CHAT.CHART.TABLE.X';
const Y_KEY = 'CHAT.CHART.TABLE.Y';

const textOf = (value: unknown): string =>
  typeof value === 'string' ? value : '';

export type ChartCopyLoader = () => Promise<typeof fromChartCopy>;

/**
 * Loads the lazy chart-copy chunk (`charts/chart.copy.ts`,
 * which carries `@shared/charts`).
 *
 * The factory must stay a **literal** `import()`, or the bundler cannot split
 * the chunk and the validator becomes eager. A token rather than an inline
 * `import()` so a spec can make the load fail.
 */
export const CHART_COPY_LOADER = new InjectionToken<ChartCopyLoader>(
  'CHART_COPY_LOADER',
  {
    providedIn: 'root',
    factory: () => () => import('../../../../../charts/chart.copy'),
  },
);

/** Trailing `\n`s. A loop, not a regex, so a long run costs linear time. */
const newlinesAtEnd = (value: string): number => {
  let count = 0;
  while (count < value.length && value[value.length - 1 - count] === '\n') {
    count += 1;
  }
  return count;
};

const newlinesAtStart = (value: string): number => {
  let count = 0;
  while (count < value.length && value[count] === '\n') {
    count += 1;
  }
  return count;
};

/**
 * `before` then `after`, with at least one blank line between them when both
 * are non-empty, so a chart's pipe table stays its own markdown block. Only
 * newlines are added; neither side is edited.
 */
const joinBlocks = (before: string, after: string): string => {
  if (before === '' || after === '') {
    return before + after;
  }
  const missing = 2 - newlinesAtEnd(before) - newlinesAtStart(after);
  return before + '\n'.repeat(Math.max(0, missing)) + after;
};

@Component({
  selector: 'squadbox-message-quick-actions-copy',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    MatTooltipModule,
    ClipboardModule,
    TranslateModule,
  ],
  providers: [MarkdownPipe],
  template: `
    <button
      sdIconButton
      variant="icon"
      color="neutral"
      size="xs"
      [matTooltip]="tooltipText()"
      matTooltipPosition="above"
      (click)="onCopy()"
    >
      <svg sdUseUIBasicIcon="copy"></svg>
    </button>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class MessageQuickActionsCopyComponent implements OnInit, OnDestroy {
  private readonly markdownPipe = inject(MarkdownPipe);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly chartCopyLoader = inject(CHART_COPY_LOADER);
  private onScrollResetTooltipSubscription?: Subscription;
  public readonly messageContent = input.required<string>();
  public readonly chatId = input.required<number>();
  public readonly messageId = input.required<number>();
  public readonly tooltipText = signal(
    this.translateService.instant('CHAT.COPY'),
  );

  @ViewChild(MatTooltip) matTooltip!: MatTooltip;

  ngOnInit(): void {
    // Find the scrollable message container by traversing up the DOM
    const scrollableElement = this.findScrollableContainer();

    if (scrollableElement) {
      this.onScrollResetTooltipSubscription = fromEvent(
        scrollableElement,
        'scroll',
      )
        .pipe(
          throttleTime(SCROLL_THROTTLE_TIME_MS),
          tap(() => this.resetTooltip()),
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    if (this.onScrollResetTooltipSubscription) {
      this.onScrollResetTooltipSubscription.unsubscribe();
    }
  }

  private findScrollableContainer(): Element | null {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let element = this.elementRef.nativeElement.parentElement;

    // Traverse up the DOM tree to find the scrollable container
    while (element && element !== document.body) {
      const computedStyle = window.getComputedStyle(element as Element);
      if (
        computedStyle.overflowY === 'auto' ||
        computedStyle.overflowY === 'scroll'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return element;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      element = element.parentElement;
    }

    return null;
  }

  /**
   * A message with a chart block copies each chart as its
   * table. The split uses the screen's lexer and recognizer, so copy and screen
   * agree on what a chart is. A message with no chart block takes the path
   * that shipped before, unchanged.
   */
  public async onCopy() {
    const content = this.messageContent();
    const pieces = splitAtChartBlocks(content);

    if (pieces.some((piece) => piece.kind === 'chart')) {
      await this.copyWithCharts(pieces);
    } else {
      await this.copyWithoutCharts(content);
    }

    this.showTooltipCopied();
  }

  /** The shipped path, byte for byte: one render of the whole message. */
  private async copyWithoutCharts(content: string): Promise<void> {
    const html = this.sanitizer.sanitize(
      SecurityContext.HTML,
      await this.markdownPipe.transform(content),
    );

    if (html) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([content], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([clipboardItem]);

      // Fire message_copied event after successful clipboard write (SB-3513 T7)
      this.sendMessageCopied();
    }
  }

  /**
   * Prose pieces render as before, one at a time; each chart piece is its
   * table (or the notice), built without `MarkdownPipe`. **No chart string
   * ever reaches `MarkdownPipe`**: that is what keeps a label such as
   * `[a](https://x)` from becoming a live link.
   *
   * Prose rendering stays outside any `try`: a prose throw rejects `onCopy` as
   * it did before, and carries no chart text.
   */
  private async copyWithCharts(
    pieces: readonly ChartSplitPiece[],
  ): Promise<void> {
    const strings = this.chartCopyStrings();
    const chartCopy = await this.loadChartCopy();

    let html = '';
    let plain = '';
    let afterChart = false;
    const tables: fromSeriousSystem.SdResponseTable[] = [];

    for (const piece of pieces) {
      if (piece.kind === 'prose') {
        html +=
          this.sanitizer.sanitize(
            SecurityContext.HTML,
            await this.markdownPipe.transform(piece.raw),
          ) ?? '';
        plain = afterChart ? joinBlocks(plain, piece.raw) : plain + piece.raw;
        afterChart = false;
        continue;
      }

      const copied = this.chartPiece(chartCopy, piece.source, strings);
      html += copied.html;
      plain = joinBlocks(plain, copied.plain);
      if (copied.table !== null) {
        tables.push(copied.table);
      }
      afterChart = true;
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);

    this.sendMessageCopied();
    // One `table_copied` per chart that copied as a table, after the write.
    for (const table of tables) {
      this.analyticsService
        .sendTableCopiedEvent(fromSeriousSystem.tableActionMetrics(table))
        .subscribe();
    }
  }

  /** The lazy chunk, or `null` when it fails to load: every chart is then the notice. */
  private async loadChartCopy(): Promise<typeof fromChartCopy | null> {
    try {
      return await this.chartCopyLoader();
    } catch {
      return null;
    }
  }

  /**
   * One chart piece. Every throw becomes the notice, and the `catch` holds no
   * binding: a V8 `JSON.parse` error quotes its input, and an unhandled
   * rejection would carry chart text to Sentry.
   *
   * The chart HTML holds only escaped strings in fixed tags; it still passes
   * through Angular's sanitizer as a plain string, as defense in depth.
   */
  private chartPiece(
    chartCopy: typeof fromChartCopy | null,
    source: string,
    strings: fromChartCopy.ChartCopyStrings,
  ): ChartCopyPiece {
    if (chartCopy !== null) {
      try {
        const piece = chartCopy.chartCopyPiece(source, strings);
        return {
          ...piece,
          html: this.sanitizer.sanitize(SecurityContext.HTML, piece.html) ?? '',
        };
      } catch {
        // Fall through to the notice.
      }
    }
    return chartNoticePiece(strings.unavailable);
  }

  private chartCopyStrings(): fromChartCopy.ChartCopyStrings {
    const text = (key: string): string =>
      textOf(this.translateService.instant(key));
    return {
      labels: { series: text(SERIES_KEY), x: text(X_KEY), y: text(Y_KEY) },
      // An empty caption falls back to its key inside `chartCopyPiece`.
      caption: text(ILLUSTRATIVE_KEY),
      // Resolved here, so the eager notice (failed import, a throw) and the
      // lazy one both get it: a chart never copies as an empty string.
      unavailable: text(COPY_UNAVAILABLE_KEY) || COPY_UNAVAILABLE_KEY,
    };
  }

  private sendMessageCopied(): void {
    this.analyticsService
      .sendMessageCopiedEvent({
        chatId: this.chatId(),
        messageId: this.messageId(),
      })
      .subscribe();
  }

  private showTooltipCopied(): void {
    this.matTooltip.hideDelay = COPIED_TEXT_TIMEOUT_MS;
    this.tooltipText.set(this.translateService.instant('CHAT.COPIED'));
    this.matTooltip.show();

    setTimeout(() => {
      this.resetTooltip();
    }, COPIED_TEXT_TIMEOUT_MS);
  }

  private resetTooltip(): void {
    this.matTooltip.hideDelay = 0;
    this.matTooltip.hide();
    setTimeout(() => {
      this.tooltipText.set(this.translateService.instant('CHAT.COPY'));
    }, TOOLTIP_HIDE_FADE_MS);
  }
}
